import type { CashSessionRow } from '../../cash/domain-api';
import type { BranchCashSessionReportRow } from '../domain/reportsTypes';
import {
  reportsQueryScopeToRpcParams,
  type ReportsQueryScope,
} from '../domain/reportsQueryScope';

export type {
  ReportsOperacionSource,
  ReportsOperacionKpiSources,
  BranchCashSessionReportRow,
  PosExceptionSummary,
  SalesDailyRow,
  ReportsVentasProductScope,
  SalesByProductSortKey,
  BranchSalesByProductRow,
  BranchSalesByProductResponse,
} from '../domain/reportsTypes';

/** Reintento breve ante fallos intermitentes de red / RPC (sin dependencias RN). */
export const REMOTE_FETCH_RETRIES = 1;
export const REMOTE_FETCH_RETRY_DELAY_MS = 400;

export async function fetchWithRetry<T>(fn: () => Promise<T | null>): Promise<T | null> {
  let last = await fn();
  if (last != null) return last;
  for (let i = 0; i < REMOTE_FETCH_RETRIES; i++) {
    await new Promise((r) => setTimeout(r, REMOTE_FETCH_RETRY_DELAY_MS));
    last = await fn();
    if (last != null) return last;
  }
  return null;
}

export { isOnline } from '../../shell/connectivity/connectivityInfra';

export function mapRpcError(e: unknown): string {
  const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : String(e);
  return msg;
}

export function parseCashSessionRow(raw: Record<string, unknown> | null | undefined): CashSessionRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id != null ? String(raw.id) : '';
  if (!id) return null;
  return {
    id,
    branchId: String(raw.branch_id ?? ''),
    openedAt: String(raw.opened_at ?? ''),
    openingAmount: Number(raw.opening_amount ?? 0),
    openedBy: raw.opened_by != null ? String(raw.opened_by) : null,
    status: raw.status === 'closed' ? 'closed' : 'open',
    closedAt: raw.closed_at != null ? String(raw.closed_at) : null,
    closingAmount: raw.closing_amount != null ? Number(raw.closing_amount) : null,
    expectedAmount: raw.expected_amount != null ? Number(raw.expected_amount) : null,
    difference: raw.difference != null ? Number(raw.difference) : null,
    note: raw.note != null ? String(raw.note) : null,
    closedBy: raw.closed_by != null ? String(raw.closed_by) : null,
  };
}

export function parseBranchCashSessionReportRow(raw: Record<string, unknown>): BranchCashSessionReportRow | null {
  const id = raw.id != null ? String(raw.id) : '';
  if (!id) return null;
  const st = raw.status === 'open' || raw.status === 'closed' ? raw.status : 'closed';
  return {
    id,
    openedAt: String(raw.opened_at ?? ''),
    openingAmount: Number(raw.opening_amount ?? 0),
    status: st,
    openedByUserId: raw.opened_by != null ? String(raw.opened_by) : null,
    openerLabel: String(raw.opener_label ?? '—'),
    closedAt: raw.closed_at != null ? String(raw.closed_at) : null,
    difference: raw.difference != null ? Number(raw.difference) : null,
    note: raw.note != null ? String(raw.note) : null,
  };
}

type RpcVentasFilterParams = ReturnType<typeof reportsQueryScopeToRpcParams>;

/** Parámetros RPC de alcance Ventas/Personal (snapshot, by_cashier, personal_facts). */
export function rpcVentasProductFilters(scope?: ReportsQueryScope | null): RpcVentasFilterParams {
  return reportsQueryScopeToRpcParams(scope ?? null);
}

/** Devuelve el primer valor ISO no-nulo de un registro RPC, probando varias claves. */
export function pickIso(r: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = r[k];
    if (v != null && String(v).trim() !== '') return String(v);
  }
  return null;
}

/** Valores de scope RPC para construir cache keys (orden estable). */
export function scopeCacheKeyParts(scope: RpcVentasFilterParams): (string | null | undefined)[] {
  return [
    scope.p_category,
    scope.p_search,
    scope.p_item_type,
    scope.p_product_id,
    scope.p_brand,
    scope.p_supplier_id,
    scope.p_customer_id,
    scope.p_cashier_user_id,
  ];
}
